/* eslint-disable no-param-reassign */
import React, { forwardRef, ReactElement, useEffect, useState } from "react";
import ct from "class-types.macro";
import Department from "./Department";
import DepartmentJobs from "./DepartmentJobs";
import DepartmentsGrid from "./DepartmentsGrid";
import JobListing from "./JobListing";
import JobsLocationsList from "./JobsLocationsList";
import { Filter } from "./Filter";
import { Internship } from "./Constants";

interface Props {
  jobListings: JobListing[];
  className?: string;
  topRightButton?: ReactElement;
}

const MultipleDepartmentJobBrowser = forwardRef<HTMLDivElement, Props>(
  ({ jobListings, className = "", topRightButton }, ref) => {
    const [stateNames, setStateNames] = useState<string[]>([]);
    const workingLocationIdRemote = 2;

    // Things to filter by
    const [filter, setFilter] = useState<Filter | undefined>(undefined);
    const [filterByDepartment, setFilterByDepartment] = useState<
      Department | undefined
    >(undefined);

    // Things that get filtered
    const [filteredJobListings, setFilteredJobListings] = useState<
      JobListing[]
    >(jobListings);
    const [filteredDepartments, setFilteredDepartments] = useState<
      Department[]
    >([]);

    // Filter departments
    useEffect(() => {
      const departments = jobListings.reduce<Department[]>(
        (departments, job) => {
          if (
            filter !== undefined &&
            (filter.allowRemote
              ? !(job.workingLocationId === workingLocationIdRemote)
              : job.stateName !== filter.state)
          ) {
            return departments;
          }

          let jobsDepartment = departments.find((single) =>
            filter?.allowRemote
              ? job.workingLocationId === workingLocationIdRemote &&
                single.name === job.departmentName
              : single.name === job.departmentName
          );

          if (!jobsDepartment) {
            jobsDepartment = {
              id: job.departmentId,
              seoUrlKey: job.departmentSeoUrlKey,
              name: job.departmentName,
              jobCount: 0,
            };

            departments.push(jobsDepartment);
          }

          jobsDepartment.jobCount += 1;

          // But is the job also an internship?  Add it to the "Internship" pseudo-department
          if (job.employmentType === Internship) {
            let internJobDepartment = departments.find(
              (department) => department.name === Internship
            );
            if (!internJobDepartment) {
              internJobDepartment = {
                id: 1,
                name: Internship,
                seoUrlKey: Internship.toLowerCase(),
                jobCount: 0,
              };
              departments.push(internJobDepartment);
            }
            internJobDepartment.jobCount += 1;
          }

          return departments;
        },
        []
      );

      setFilteredDepartments(departments);
    }, [filter, jobListings]);

    // Filter states
    useEffect(() => {
      const newStates = jobListings.reduce<string[]>((allLocations, job) => {
        const matchesDepartmentFilter =
          filterByDepartment === undefined ||
          job.departmentId === filterByDepartment.id;

        if (!allLocations.includes(job.stateName) && matchesDepartmentFilter) {
          allLocations.push(job.stateName);
        }

        return allLocations;
      }, []);

      setStateNames(newStates.sort());
    }, [filterByDepartment, jobListings]);

    // Filter jobs
    useEffect(() => {
      const newFilteredJobs = jobListings.filter((job) => {
        const matchesStateFilter =
          filter === undefined || job.stateName === filter.state;
        const matchesDepartmentFilter =
          filterByDepartment === undefined ||
          job.departmentId === filterByDepartment.id ||
          (filterByDepartment.name === Internship &&
            job.employmentType === Internship);

        return filter?.allowRemote
          ? job.workingLocationId === workingLocationIdRemote &&
              matchesDepartmentFilter
          : matchesStateFilter && matchesDepartmentFilter;
      });

      setFilteredJobListings(newFilteredJobs);
    }, [filterByDepartment, filter, jobListings]);

    const onClickViewAllDepartments = () => {
      setFilterByDepartment(undefined);
    };

    return (
      <div
        id="careers-positions"
        className={`careers-positions ${className}`}
        ref={ref}
      >
        <div className={ct("container")}>
          <div className="careers-positions-top">
            {topRightButton}
            <h2 className={`careers-positions__title ${ct("pt-2")}`}>Jobs</h2>
            <div className="careers-positions__separator" />
            <div className="text">
              Have you got what it takes? We&apos;re looking for driven,
              forward-thinking people.
            </div>
          </div>
          <div className="careers-positions-left">
            <JobsLocationsList
              stateNames={stateNames}
              onFilter={setFilter}
              jobListings={filteredJobListings}
            />
          </div>
          <div className="careers-positions-right">
            {filterByDepartment ? (
              <DepartmentJobs
                jobListings={filteredJobListings}
                department={filterByDepartment}
                onClickViewAll={onClickViewAllDepartments}
              />
            ) : (
              <DepartmentsGrid
                departments={filteredDepartments}
                onSelectDepartment={setFilterByDepartment}
              />
            )}
            <div
              id="jobListings"
              className="careers-positions-job-listing block hidden"
            >
              {jobListings.map((job) => {
                const url = `/careers/department/${job.departmentSeoUrlKey}/${job.id}/${job.jobNumber}/`;

                return (
                  <a
                    key={`${job.id}-department-links`}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="job-listing"
                  >
                    <span>
                      {job.title} - {job.city}, {job.stateName}
                    </span>
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default MultipleDepartmentJobBrowser;
